/**
 * React Hooks for Remediation Guidance
 *
 * Provides useQuery hooks for fetching and caching remediation guidance.
 */

import { useQuery, useQueries } from "@tanstack/react-query";
import type {
  RemediationGuidance,
  FallbackRemediationGuidance,
  IssueGroupWithGuidance,
} from "@accessimonitor/shared";
import {
  getRemediationGuidance,
  listRemediationGuidance,
  enrichIssueGroupsWithGuidance,
  getRemediationCoverageStats,
} from "../api/remediation.js";

/**
 * Hook to fetch remediation guidance for a specific issue key.
 * @param issueKey - The accessibility issue key (e.g., "IMG_ALT_TEXT_MISSING")
 * @param enabled - If false, the query will not execute (default: true)
 */
export function useRemediationGuidance(
  issueKey: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["remediation", "guidance", issueKey],
    queryFn: () => {
      if (!issueKey) throw new Error("issueKey is required");
      return getRemediationGuidance(issueKey);
    },
    enabled: enabled && !!issueKey,
    staleTime: 1000 * 60 * 5, // 5 minutes — remediation guidance is stable
  });
}

/**
 * Hook to fetch all available remediation guidance mappings.
 * Useful for admin pages or documentation surfaces.
 */
export function useListRemediationGuidance() {
  return useQuery({
    queryKey: ["remediation", "guidance", "list"],
    queryFn: listRemediationGuidance,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to enrich issue groups with remediation guidance.
 * @param issueGroups - Array of issue group objects to enrich
 * @param enabled - If false, the query will not execute (default: true)
 */
export function useEnrichIssueGroups(
  issueGroups: Array<{
    id: string;
    issue_key: string;
    title: string;
    count: number;
    severity?: string;
  }> | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ["remediation", "enrich", issueGroups],
    queryFn: () => {
      if (!issueGroups) throw new Error("issueGroups is required");
      return enrichIssueGroupsWithGuidance(issueGroups);
    },
    enabled: enabled && issueGroups != null && issueGroups.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get remediation coverage statistics for a set of issue keys.
 * @param issueKeys - Array of issue keys to check coverage for
 * @param enabled - If false, the query will not execute (default: true)
 */
export function useRemediationCoverage(
  issueKeys: string[] | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ["remediation", "coverage", issueKeys],
    queryFn: () => {
      if (!issueKeys) throw new Error("issueKeys is required");
      return getRemediationCoverageStats(issueKeys);
    },
    enabled: enabled && issueKeys != null && issueKeys.length > 0,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to fetch multiple remediation guidance entries in parallel.
 * Useful when you need guidance for multiple specific issues.
 */
export function useRemediationGuidances(
  issueKeys: (string | null | undefined)[] | null,
  enabled = true,
) {
  const validKeys = issueKeys?.filter(Boolean) ?? [];

  const results = useQueries({
    queries: validKeys.map((key) => ({
      queryKey: ["remediation", "guidance", key],
      queryFn: () => {
        if (!key) throw new Error("key is required");
        return getRemediationGuidance(key);
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
    })),
    combine: (results) => ({
      data: results.map((r) => r.data).filter(Boolean),
      isPending: results.some((r) => r.isPending),
      isLoading: results.some((r) => r.isLoading),
      isError: results.some((r) => r.isError),
      errors: results.filter((r) => r.error).map((r) => r.error),
    }),
  });

  return {
    guidances: results.data,
    isPending: results.isPending && enabled && validKeys.length > 0,
    isLoading: results.isLoading && enabled && validKeys.length > 0,
    isError: results.isError && enabled && validKeys.length > 0,
    errors: results.errors,
  };
}
